"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { Loader2 } from "lucide-react"
import { useRouter, useSearchParams, usePathname } from "next/navigation"
import { Search as SearchIcon, X } from "lucide-react"
import { useDebouncedCallback } from "use-debounce"
import { Suspense } from "react"
import { cn } from "@/lib/utils"

interface SearchProps {
  basePath?: string;
  placeholder?: string;
  className?: string;
  variant?: 'default' | 'prominent';
}

function SearchInput({ basePath, placeholder = "Search galleries...", className, variant = 'default' }: SearchProps) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const { replace } = useRouter()
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(false)
  const [mounted, setMounted] = React.useState(false)
  const [isSearching, setIsSearching] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [portalEl, setPortalEl] = React.useState<HTMLElement | null>(null)

  const handleSearch = useDebouncedCallback((term: string) => {
    setIsSearching(true)
    const params = new URLSearchParams(searchParams)
    if (term) {
      params.set("q", term)
    } else {
      params.delete("q")
    }

    const path = basePath || (basePath === undefined ? "/" : pathname);
    replace(`${path}?${params.toString()}`)
  }, 300)

  const toggleSearch = () => {
    setIsExpanded(prev => {
      const next = !prev
      if (next) {
        // Focus after state update; short delay lets layout settle
        setTimeout(() => inputRef.current?.focus(), 80)
      }
      return next
    })
  }

  // Sync input value with URL search params
  React.useEffect(() => {
    if (inputRef.current) {
      const query = searchParams.get("q")?.toString() || ""
      // Only update if the input is NOT focused to avoid overwriting user input while typing
      if (document.activeElement !== inputRef.current && inputRef.current.value !== query) {
        inputRef.current.value = query
      }

      // Clear searching state once URL reflects current input
      if (inputRef.current.value === query) {
        setIsSearching(false)
      }
    }
  }, [searchParams])

  // Track mobile viewport to change layout
  React.useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth < 768)
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => window.removeEventListener('resize', updateIsMobile)
  }, [])

  React.useEffect(() => {
    setMounted(true)
    setPortalEl(document.body)
  }, [])

  if (variant === 'prominent') {
    return (
      <div className={cn("relative w-full", className,)}>
        <div className="relative flex items-center w-full group">
          <SearchIcon className="absolute left-4 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-white/90" />
          <input
            className="h-14 w-full appearance-none rounded-md border-0 bg-slate-100 dark:bg-slate-800/50 px-12 text-lg shadow-sm transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 uppercase tracking-wider font-medium hover:bg-slate-200/50 dark:hover:bg-slate-800/80"
            placeholder={placeholder}
            defaultValue={searchParams.get("q")?.toString()}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cn("relative flex items-center min-w-0", className)}>
      {!isMobile && (
        <div
          className={cn(
            "relative flex items-center overflow-hidden transition-[width,opacity] duration-200 ease-in-out",
            isExpanded
              ? "flex-1 opacity-100 w-[min(24rem,calc(100vw-5rem))] max-w-full sm:w-64 sm:max-w-xs"
              : "w-0 max-w-0 opacity-0",
          )}
        >
          <input
            ref={inputRef}
            className="h-9 w-full appearance-none rounded-md border border-input bg-background px-3 pr-8 py-1 text-sm shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={placeholder}
            defaultValue={searchParams.get("q")?.toString()}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {isSearching && (
            <Loader2 className="pointer-events-none absolute right-2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      )}

      <button
        onClick={toggleSearch}
        className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {isExpanded ? (
          <X className="h-4 w-4" />
        ) : (
          <SearchIcon className="h-4 w-4" />
        )}
        <span className="sr-only">Search</span>
      </button>

      {isMobile && isExpanded && mounted && portalEl && createPortal(
        <>
          <div
            className="fixed inset-0 z-[55] bg-black/10"
            onClick={() => setIsExpanded(false)}
          />
          <div className="fixed inset-x-4 top-[calc(env(safe-area-inset-top)+64px)] z-[60] sm:inset-x-6">
            <div className="relative flex items-center rounded-lg border border-input bg-background shadow-xl">
              <SearchIcon className="absolute left-3 h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                className="h-11 w-full appearance-none rounded-lg border-0 bg-transparent pl-9 pr-11 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={placeholder}
                defaultValue={searchParams.get("q")?.toString()}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {isSearching && (
                <Loader2 className="pointer-events-none absolute right-10 h-4 w-4 animate-spin text-muted-foreground" />
              )}
              <button
                onClick={() => setIsExpanded(false)}
                className="absolute right-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close search</span>
              </button>
            </div>
          </div>
        </>,
        portalEl
      )}
    </div>
  )
}

export function Search(props: SearchProps) {
  return (
    <Suspense fallback={
      <div className="relative flex items-center">
        <button className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-50">
          <SearchIcon className="h-4 w-4" />
        </button>
      </div>
    }>
      <SearchInput {...props} />
    </Suspense>
  )
}
